<template>
  <div class="payouts-tab">
    <section class="panel">
      <div class="panel-header">
        <div>
          <h3>Prepare a payout batch</h3>
          <p>
            Reserves the partner's available, reconciled balance (at or above the payout minimum) into one batch.
            Amounts are owed in USD and paid in the currency each sale settled in, at that sale's Stripe exchange rate.
          </p>
        </div>
      </div>
      <el-form :inline="true" size="small" @submit.native.prevent="prepareBatch">
        <el-form-item label="Partner ID">
          <el-input v-model.trim="prepareForm.affiliate_id" maxlength="24" placeholder="24-character user ID" style="width: 260px" class="mono-input" />
        </el-form-item>
        <el-form-item label="Stripe account">
          <el-tag type="info">new</el-tag>
        </el-form-item>
        <el-form-item>
          <el-checkbox v-model="prepareForm.livemode">Live mode</el-checkbox>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" native-type="submit" :loading="isPending('prepare')" :disabled="anyPending">Prepare batch</el-button>
        </el-form-item>
      </el-form>
      <el-alert
        v-if="!prepareForm.livemode"
        title="Test mode: only test-mode ledger rows are included."
        type="warning"
        show-icon
        :closable="false"
        class="panel-alert"
      />
      <el-alert v-if="prepared" type="success" :closable="false" show-icon class="panel-alert">
        <template slot="title">
          Batch <span class="mono">{{ prepared.batch_id }}</span> prepared: {{ formatUsdCents(prepared.amount_cents) }}
          → {{ formatMinorUnits(prepared.payout_total, prepared.payout_currency) }}
          ({{ prepared.items }} item{{ prepared.items === 1 ? '' : 's' }}, {{ methodLabel(prepared.payout_method) }}). Review and approve it below.
        </template>
      </el-alert>
    </section>

    <section class="panel">
      <div class="panel-header">
        <div>
          <h3>Payout batches</h3>
          <p>Prepared → approved → dispatched through Stripe Connect (transferred) or recorded as a manual bank / Wise payout (paid).</p>
        </div>
      </div>
      <el-form :inline="true" size="small" class="filters" @submit.native.prevent="fetchBatches">
        <el-form-item label="Partner ID">
          <el-input v-model.trim="filters.affiliate_id" maxlength="24" clearable placeholder="All partners" style="width: 240px" class="mono-input" @clear="fetchBatches" />
        </el-form-item>
        <el-form-item label="Status">
          <el-select v-model="filters.status" style="width: 150px" @change="fetchBatches">
            <el-option label="All" value="all" />
            <el-option v-for="option in statusOptions" :key="option.value" :label="option.label" :value="option.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="Limit">
          <el-select v-model="filters.limit" style="width: 90px" @change="fetchBatches">
            <el-option v-for="limit in [25, 50, 100]" :key="limit" :label="limit" :value="limit" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button icon="el-icon-search" native-type="submit" :loading="loading">Search</el-button>
        </el-form-item>
      </el-form>

      <el-table v-loading="loading" :data="batches" border size="small" style="width: 100%" row-key="_id" empty-text="No payout batches">
        <el-table-column type="expand">
          <template slot-scope="scope">
            <div class="expand-body">
              <el-table :data="scope.row.items || []" size="mini" border empty-text="No items">
                <el-table-column label="Payment key" min-width="200">
                  <template slot-scope="item"><span class="mono" :title="item.row.payment_key">{{ shortId(item.row.payment_key, 24) }}</span></template>
                </el-table-column>
                <el-table-column label="Owed (USD)" width="120">
                  <template slot-scope="item">{{ formatUsdCents(item.row.amount_cents) }}</template>
                </el-table-column>
                <el-table-column label="Payout" width="130">
                  <template slot-scope="item">{{ formatMinorUnits(item.row.payout_amount, scope.row.payout_currency) }}</template>
                </el-table-column>
                <el-table-column label="Exchange rate" width="150">
                  <template slot-scope="item">{{ rateLabel(item.row.exchange_rate, scope.row.payout_currency) }}</template>
                </el-table-column>
                <el-table-column label="Item status" width="150">
                  <template slot-scope="item">
                    <el-tag size="mini" :type="itemStatusTag(item.row.item_status)">{{ itemStatusLabel(item.row.item_status) }}</el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="Transfer" min-width="170">
                  <template slot-scope="item"><span class="mono">{{ item.row.transfer_id || '-' }}</span></template>
                </el-table-column>
                <el-table-column v-if="reconcilableCount(scope.row)" label="Actions" width="120">
                  <template slot-scope="item">
                    <el-button
                      v-if="canReconcile(scope.row, item.row)"
                      size="mini"
                      type="primary"
                      plain
                      :loading="isPending(`reconcile:${idOf(scope.row)}:${item.row.payment_key}`)"
                      :disabled="anyPending"
                      @click="reconcileItem(scope.row, item.row)"
                    >
                      Reconcile
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>
              <el-descriptions :column="3" size="mini" border class="expand-meta">
                <el-descriptions-item label="Batch ID"><span class="mono">{{ idOf(scope.row) }}</span></el-descriptions-item>
                <el-descriptions-item label="Stripe account">{{ scope.row.stripe_account || '-' }} · {{ scope.row.livemode === false ? 'test' : 'live' }}</el-descriptions-item>
                <el-descriptions-item label="External reference">{{ scope.row.external_reference || '-' }}</el-descriptions-item>
                <el-descriptions-item label="Approved">{{ formatTime(scope.row.approved_at) }}</el-descriptions-item>
                <el-descriptions-item label="Dispatch started">{{ formatTime(scope.row.dispatch_started_at) }}</el-descriptions-item>
                <el-descriptions-item label="Transferred">{{ formatTime(scope.row.transferred_at) }}</el-descriptions-item>
                <el-descriptions-item label="Paid at">{{ formatTime(scope.row.paid_at) }}</el-descriptions-item>
                <el-descriptions-item label="Canceled">{{ formatTime(scope.row.canceled_at) }}</el-descriptions-item>
                <el-descriptions-item label="Cancel reason">{{ scope.row.cancel_reason || '-' }}</el-descriptions-item>
              </el-descriptions>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Batch" min-width="170">
          <template slot-scope="scope">
            <div class="mono" :title="idOf(scope.row)">{{ shortId(scope.row, 14) }}</div>
            <div class="muted">{{ formatTime(scope.row.created_at) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Partner" min-width="190">
          <template slot-scope="scope"><span class="mono">{{ scope.row.affiliate_id || '-' }}</span></template>
        </el-table-column>
        <el-table-column label="Method" width="110">
          <template slot-scope="scope">
            <div>{{ methodLabel(scope.row.payout_method) }}</div>
            <el-tag v-if="scope.row.livemode === false" size="mini" type="warning">Test</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Status" width="130">
          <template slot-scope="scope">
            <el-tag size="small" :type="statusTag(scope.row.status)">{{ statusLabel(scope.row.status) }}</el-tag>
            <div v-if="reconcilableCount(scope.row)" class="reconcile-note">{{ reconcilableCount(scope.row) }} to reconcile</div>
          </template>
        </el-table-column>
        <el-table-column label="Owed (USD)" width="110">
          <template slot-scope="scope">{{ formatUsdCents(scope.row.total_cents) }}</template>
        </el-table-column>
        <el-table-column label="Payout" width="120">
          <template slot-scope="scope">
            <div class="strong">{{ formatMinorUnits(scope.row.payout_total, scope.row.payout_currency) }}</div>
            <div class="muted">{{ (scope.row.items || []).length }} item{{ (scope.row.items || []).length === 1 ? '' : 's' }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Reference" min-width="130">
          <template slot-scope="scope">
            <div>{{ scope.row.external_reference || '-' }}</div>
            <div v-if="scope.row.paid_at" class="muted">Paid {{ formatTime(scope.row.paid_at) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Actions" width="200" fixed="right">
          <template slot-scope="scope">
            <div class="actions">
              <el-button
                v-if="scope.row.status === 'prepared'"
                size="mini"
                type="primary"
                :loading="isPending(`approve:${idOf(scope.row)}`)"
                :disabled="anyPending"
                @click="approveBatch(scope.row)"
              >
                Approve
              </el-button>
              <el-tooltip
                v-if="canDispatch(scope.row)"
                :disabled="dispatchEnabled !== false"
                content="Stripe payout dispatch is turned off on the server (LF_AFFILIATE_CONNECT_DISPATCH_ENABLED)."
                placement="top"
              >
                <span class="tooltip-anchor">
                  <el-button
                    size="mini"
                    type="primary"
                    :loading="isPending(`dispatch:${idOf(scope.row)}`)"
                    :disabled="anyPending || dispatchEnabled === false"
                    @click="dispatchBatch(scope.row)"
                  >
                    {{ scope.row.status === 'dispatching' ? 'Resume dispatch' : 'Dispatch via Stripe' }}
                  </el-button>
                </span>
              </el-tooltip>
              <el-button
                v-if="canRecordManual(scope.row)"
                size="mini"
                type="primary"
                :disabled="anyPending"
                @click="openRecord(scope.row)"
              >
                {{ scope.row.status === 'recording' ? 'Finish recording' : 'Record payout' }}
              </el-button>
              <el-button
                v-if="['prepared', 'approved', 'dispatching'].includes(scope.row.status)"
                size="mini"
                type="danger"
                plain
                :loading="isPending(`cancel:${idOf(scope.row)}`)"
                :disabled="anyPending"
                @click="cancelBatch(scope.row)"
              >
                Cancel
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </section>

    <el-dialog title="Record manual payout" :visible.sync="recordVisible" width="600px" :close-on-click-modal="false">
      <template v-if="recordTarget">
        <el-alert
          title="Record only after the bank or Wise transfer has actually been sent."
          description="The approved amounts settle the ledger. Refunds that arrived after approval become a negative balance that offsets later earnings."
          type="warning"
          show-icon
          :closable="false"
          class="panel-alert"
        />
        <el-descriptions :column="1" size="small" border class="panel-alert">
          <el-descriptions-item label="Batch"><span class="mono">{{ idOf(recordTarget) }}</span></el-descriptions-item>
          <el-descriptions-item label="Partner"><span class="mono">{{ recordTarget.affiliate_id }}</span></el-descriptions-item>
          <el-descriptions-item label="Amount to send">
            <span class="strong">{{ formatMinorUnits(recordTarget.payout_total, recordTarget.payout_currency) }}</span>
            <span class="muted"> (owed {{ formatUsdCents(recordTarget.total_cents) }})</span>
          </el-descriptions-item>
        </el-descriptions>
        <el-form label-position="top" size="small" @submit.native.prevent>
          <el-form-item label="Transfer reference" required>
            <el-input
              v-model.trim="recordForm.external_reference"
              maxlength="200"
              show-word-limit
              :disabled="recordTarget.status === 'recording'"
              placeholder="Bank or Wise transfer ID"
            />
            <div v-if="recordTarget.status === 'recording'" class="muted field-help">Recording already started with this reference; finishing must reuse it.</div>
          </el-form-item>
          <el-form-item label="Paid on" required>
            <el-date-picker v-model="recordForm.paid_at" type="datetime" :clearable="false" :picker-options="paidPickerOptions" style="width: 220px" />
          </el-form-item>
        </el-form>
      </template>
      <span slot="footer">
        <el-button @click="recordVisible = false">Cancel</el-button>
        <el-button
          type="primary"
          :loading="recordTarget && isPending(`record:${idOf(recordTarget)}`)"
          :disabled="anyPending || !recordForm.external_reference || !recordForm.paid_at"
          @click="recordManualPayout"
        >
          Record as paid
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  approveAffiliatePayoutBatch,
  cancelAffiliatePayoutBatch,
  dispatchAffiliatePayoutBatch,
  getAffiliateProgramStatus,
  listAffiliatePayoutBatches,
  prepareAffiliatePayoutBatch,
  reconcileAffiliatePayoutItem,
  recordManualAffiliatePayout
} from '@/apis/lfAdmin'
import affiliateMixin, { OBJECT_ID_PATTERN, PAYOUT_BATCH_STATUSES, labelFor } from './affiliateMixin'

// Earliest paid date the backend accepts (unix 1700000000 = 2023-11-14).
const EARLIEST_PAID_AT = 1700000000

const ITEM_STATUSES = {
  not_sent: { label: 'Not sent', tag: 'info' },
  dispatching: { label: 'Sending', tag: 'warning' },
  unknown: { label: 'Unknown outcome', tag: 'danger' },
  confirmed_absent: { label: 'Confirmed absent', tag: 'warning' },
  provider_confirmed: { label: 'Provider confirmed', tag: '' },
  transferred: { label: 'Transferred', tag: 'success' },
  paid_manual: { label: 'Paid manually', tag: 'success' }
}

// Only these outcomes are uncertain; reconciliation asks Stripe what happened.
const RECONCILABLE_ITEM_STATUSES = ['dispatching', 'unknown']

function hasControlCharacters(text) {
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i)
    if (code < 32 || code === 127) return true
  }
  return false
}

export default {
  name: 'AffiliatePayoutsTab',
  mixins: [affiliateMixin],
  props: {
    affiliateId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      prepareForm: {
        affiliate_id: '',
        livemode: true
      },
      prepared: null,
      statusOptions: PAYOUT_BATCH_STATUSES,
      filters: {
        affiliate_id: '',
        status: 'all',
        limit: 50
      },
      loading: false,
      batches: [],
      // null until the program status loads; only an explicit false disables dispatch.
      dispatchEnabled: null,
      recordVisible: false,
      recordTarget: null,
      recordForm: {
        external_reference: '',
        paid_at: null
      },
      paidPickerOptions: {
        disabledDate(date) {
          const time = date.getTime()
          return time > Date.now() + 86400 * 1000 || time < (EARLIEST_PAID_AT - 86400) * 1000
        }
      }
    }
  },
  watch: {
    affiliateId: {
      immediate: true,
      handler(id) {
        if (id && OBJECT_ID_PATTERN.test(id) && id !== this.filters.affiliate_id) {
          this.filters.affiliate_id = id
          this.prepareForm.affiliate_id = id
          if (this.$el) this.fetchBatches()
        }
      }
    }
  },
  mounted() {
    this.fetchBatches()
    this.fetchProgramStatus()
  },
  methods: {
    statusLabel(status) {
      return labelFor(PAYOUT_BATCH_STATUSES, status)
    },
    statusTag(status) {
      const map = { prepared: '', approved: 'warning', dispatching: 'warning', recording: 'warning', transferred: 'success', paid: 'success', canceled: 'info' }
      return map[status] || ''
    },
    methodLabel(method) {
      if (method === 'connect') return 'Stripe Connect'
      if (method === 'manual') return 'Manual'
      return method || '-'
    },
    rateLabel(rate, currency) {
      const code = String(currency || '').toUpperCase()
      if (code === 'USD') return '1:1 (USD)'
      if (rate === null || rate === undefined || rate === '') return '-'
      return `1 USD = ${Number(rate)} ${code}`
    },
    async fetchProgramStatus() {
      const data = await this.loadData(() => getAffiliateProgramStatus(), 'Failed to load payout switches')
      if (data && typeof data.dispatch_enabled === 'boolean') this.dispatchEnabled = data.dispatch_enabled
    },
    itemStatusLabel(status) {
      return (ITEM_STATUSES[status] && ITEM_STATUSES[status].label) || status || '-'
    },
    itemStatusTag(status) {
      return (ITEM_STATUSES[status] && ITEM_STATUSES[status].tag) || 'info'
    },
    canReconcile(batch, item) {
      return batch.status === 'dispatching' && RECONCILABLE_ITEM_STATUSES.includes(item.item_status)
    },
    reconcilableCount(batch) {
      return (batch.items || []).filter((item) => this.canReconcile(batch, item)).length
    },
    canDispatch(row) {
      return row.payout_method === 'connect' && ['approved', 'dispatching'].includes(row.status)
    },
    canRecordManual(row) {
      return row.payout_method === 'manual' && ['approved', 'recording'].includes(row.status)
    },
    async fetchBatches() {
      const params = { limit: this.filters.limit }
      if (this.filters.affiliate_id) {
        if (!OBJECT_ID_PATTERN.test(this.filters.affiliate_id)) {
          this.$message.warning('Partner ID must be a 24-character hex ID.')
          return
        }
        params.affiliate_id = this.filters.affiliate_id
      }
      if (this.filters.status !== 'all') params.status = this.filters.status
      this.loading = true
      const data = await this.loadData(() => listAffiliatePayoutBatches(params), 'Failed to load payout batches')
      if (data !== undefined) {
        this.batches = Array.isArray(data) ? data : []
        if (params.affiliate_id) this.$emit('affiliate-change', params.affiliate_id)
      }
      this.loading = false
    },
    async prepareBatch() {
      const affiliateId = this.prepareForm.affiliate_id
      if (!OBJECT_ID_PATTERN.test(affiliateId)) {
        this.$message.warning('Partner ID must be a 24-character hex ID.')
        return
      }
      const livemode = this.prepareForm.livemode === true
      const confirmed = await this.confirmAction({
        title: 'Prepare payout batch',
        lines: [
          `Prepare a ${livemode ? 'live' : 'test'} payout batch for partner ${affiliateId} on the "new" Stripe account?`,
          'Available commission is reserved for this batch until it is paid or canceled. Nothing is sent until the batch is approved and dispatched or recorded.'
        ],
        confirmText: 'Prepare batch',
        type: 'info'
      })
      if (!confirmed) return
      const result = await this.runMutation('prepare', () => prepareAffiliatePayoutBatch({
        affiliate_id: affiliateId,
        stripe_account: 'new',
        livemode
      }), {
        success: 'Payout batch prepared',
        failure: 'Failed to prepare payout batch'
      })
      if (result && result.data) {
        this.prepared = result.data
        this.filters.affiliate_id = affiliateId
        this.filters.status = 'all'
        this.fetchBatches()
      }
    },
    batchSummary(row) {
      return `${this.formatMinorUnits(row.payout_total, row.payout_currency)} (owed ${this.formatUsdCents(row.total_cents)}) to partner ${row.affiliate_id}`
    },
    async approveBatch(row) {
      const id = this.idOf(row)
      const confirmed = await this.confirmAction({
        title: 'Approve payout batch',
        lines: [
          `Approve batch ${id}: ${this.batchSummary(row)}?`,
          row.payout_method === 'manual'
            ? 'After approval, send the transfer outside LearningFirst and then record it here.'
            : 'After approval the batch can be dispatched as Stripe Connect transfers.'
        ],
        confirmText: 'Approve'
      })
      if (!confirmed) return
      const result = await this.runMutation(`approve:${id}`, () => approveAffiliatePayoutBatch({ batch_id: id }), {
        success: 'Batch approved',
        failure: 'Failed to approve batch'
      })
      if (result) this.fetchBatches()
    },
    async cancelBatch(row) {
      const id = this.idOf(row)
      const reason = await this.confirmWithReason({
        title: 'Cancel payout batch',
        lines: [
          `Cancel batch ${id}: ${this.batchSummary(row)}?`,
          row.status === 'dispatching'
            ? 'A dispatching batch can only be canceled if no transfer was attempted; otherwise reconcile its items instead.'
            : 'The reserved commission returns to the partner\'s available balance.'
        ],
        confirmText: 'Cancel batch',
        type: 'error'
      })
      if (reason === null) return
      const result = await this.runMutation(`cancel:${id}`, () => cancelAffiliatePayoutBatch({ batch_id: id, reason }), {
        success: 'Batch canceled',
        failure: 'Failed to cancel batch'
      })
      if (result) this.fetchBatches()
    },
    async dispatchBatch(row) {
      const id = this.idOf(row)
      const confirmed = await this.confirmAction({
        title: row.status === 'dispatching' ? 'Resume Stripe dispatch' : 'Dispatch via Stripe',
        lines: [
          `Send ${row.livemode === false ? 'TEST' : 'LIVE'} Stripe Connect transfers for batch ${id}?`,
          `${this.batchSummary(row)}, ${(row.items || []).length} transfer(s).`,
          'Money leaves the platform balance. Items with an unknown outcome stop the dispatch and must be reconciled first.'
        ],
        confirmText: 'Send transfers',
        type: 'error'
      })
      if (!confirmed) return
      const result = await this.runMutation(`dispatch:${id}`, () => dispatchAffiliatePayoutBatch({ batch_id: id }), {
        failure: 'Payout dispatch failed'
      })
      if (result) {
        const count = result.data && result.data.transferred_items
        this.$message.success(`Transfers confirmed${count !== undefined ? ` for ${count} item(s)` : ''}`)
      }
      this.fetchBatches()
    },
    openRecord(row) {
      this.recordTarget = row
      this.recordForm = {
        external_reference: row.status === 'recording' ? (row.external_reference || '') : '',
        paid_at: row.status === 'recording' && row.paid_at ? new Date(Number(row.paid_at) * 1000) : new Date()
      }
      this.recordVisible = true
    },
    async recordManualPayout() {
      const row = this.recordTarget
      if (!row) return
      const id = this.idOf(row)
      const reference = String(this.recordForm.external_reference || '').trim()
      if (!reference || hasControlCharacters(reference)) {
        this.$message.warning('Enter a transfer reference without control characters.')
        return
      }
      const paidAt = Math.floor(new Date(this.recordForm.paid_at).getTime() / 1000)
      if (!paidAt || paidAt < EARLIEST_PAID_AT || paidAt > Math.floor(Date.now() / 1000) + 86400) {
        this.$message.warning('Choose a paid date that is not in the future.')
        return
      }
      const confirmed = await this.confirmAction({
        title: 'Record manual payout',
        lines: [
          `Record batch ${id} as paid: ${this.batchSummary(row)}?`,
          `Reference: ${reference}`,
          `Paid on: ${this.formatTime(paidAt)}`,
          'This settles the ledger and cannot be undone.'
        ],
        confirmText: 'Record as paid'
      })
      if (!confirmed) return
      const result = await this.runMutation(`record:${id}`, () => recordManualAffiliatePayout({
        batch_id: id,
        external_reference: reference,
        paid_at: paidAt
      }), {
        failure: 'Failed to record manual payout'
      })
      if (result) {
        const changed = (result.data && result.data.changed_since_approval) || []
        if (changed.length) {
          this.$alert(
            this.confirmMessage([
              `${changed.length} payment(s) changed after approval (for example a refund). They were settled at the approved amounts; any difference becomes a negative balance that offsets later earnings.`,
              this.codeBlock(changed.join('\n'))
            ]),
            'Payout recorded with changes',
            { type: 'warning', confirmButtonText: 'OK' }
          ).catch(() => {})
        } else {
          this.$message.success('Manual payout recorded')
        }
        this.recordVisible = false
      }
      this.fetchBatches()
    },
    async reconcileItem(batch, item) {
      const batchId = this.idOf(batch)
      const paymentKey = item.payment_key
      const confirmed = await this.confirmAction({
        title: 'Reconcile payout item',
        lines: [
          `Check Stripe for the transfer of payment ${paymentKey} in batch ${batchId}?`,
          'A matching transfer is recorded as paid. If Stripe has no transfer 15 minutes after the attempt, the item is marked absent and can be dispatched again.'
        ],
        confirmText: 'Reconcile',
        type: 'info'
      })
      if (!confirmed) return
      const result = await this.runMutation(`reconcile:${batchId}:${paymentKey}`, () => reconcileAffiliatePayoutItem({ batch_id: batchId, payment_key: paymentKey }), {
        failure: 'Failed to reconcile payout item'
      })
      if (result) {
        const transfer = result.data && result.data.transfer_id
        this.$message.success(transfer ? `Transfer ${transfer} reconciled` : 'No transfer exists; the item can be dispatched again')
        this.fetchBatches()
      }
    }
  }
}
</script>

<style scoped>
.panel {
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
  border: 1px solid #ebeef5;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.panel-header h3 {
  margin: 0;
  color: #303133;
  font-size: 16px;
}

.panel-header p {
  margin: 4px 0 0;
  color: #909399;
  font-size: 13px;
  line-height: 1.5;
}

.panel-alert {
  margin-bottom: 12px;
}

.filters {
  margin-bottom: 4px;
}

.expand-body {
  padding: 4px 12px;
}

.expand-meta {
  margin-top: 10px;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.actions .el-button + .el-button {
  margin-left: 0;
}

.tooltip-anchor {
  display: inline-block;
}

.reconcile-note {
  margin-top: 4px;
  color: #e6a23c;
  font-size: 12px;
}

.strong {
  color: #303133;
  font-weight: 600;
}

.muted {
  color: #909399;
  font-size: 12px;
}

.mono {
  font-family: Menlo, Consolas, monospace;
  font-size: 12px;
  word-break: break-all;
}

.mono-input >>> input {
  font-family: Menlo, Consolas, monospace;
}

.field-help {
  margin-top: 4px;
  line-height: 1.4;
}
</style>
